import React, { useState, useEffect } from 'react';
import { reviewsAPI } from '../api/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  Star, 
  StarHalf,
  User,
  Loader2,
  MessageSquare,
  Calendar,
  ThumbsUp,
  Camera,
  X,
  ShieldCheck
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

// Star Rating Display Component
export const StarRating = ({ rating, size = 'md', showNumber = true }) => {
  const sizeClass = size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-6 w-6' : 'h-5 w-5';
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[...Array(fullStars)].map((_, i) => (
          <Star key={`full-${i}`} className={`${sizeClass} text-yellow-400 fill-yellow-400`} />
        ))}
        {hasHalfStar && <StarHalf className={`${sizeClass} text-yellow-400 fill-yellow-400`} />}
        {[...Array(emptyStars)].map((_, i) => (
          <Star key={`empty-${i}`} className={`${sizeClass} text-gray-300`} />
        ))}
      </div>
      {showNumber && (
        <span className={`font-semibold ${size === 'lg' ? 'text-xl' : 'text-sm'} text-gray-700 ml-1`}>
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
};

// Star Rating Input Component
export const StarRatingInput = ({ rating, setRating, disabled = false }) => {
  const [hover, setHover] = useState(0);

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onClick={() => setRating(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="focus:outline-none disabled:cursor-not-allowed"
        >
          <Star
            className={`h-8 w-8 transition-colors ${
              star <= (hover || rating)
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-300 hover:text-yellow-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Review Form Component
export const ReviewForm = ({ userId, userName, jobId, projectId, jobTitle, onSuccess, onCancel }) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [photos, setPhotos] = useState([]); // array of {url, file_id}
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = React.useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const validate = () => {
    const newErrors = {};
    if (rating === 0) newErrors.rating = 'Please select a rating';
    if (!title.trim() || title.trim().length < 3) newErrors.title = 'Title must be at least 3 characters';
    if (!content.trim() || content.trim().length < 10) newErrors.content = 'Review must be at least 10 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (photos.length + files.length > 3) {
      toast({ title: 'Maximum 3 photos', description: 'Please select fewer photos', variant: 'destructive' });
      return;
    }
    setUploadingPhoto(true);
    try {
      const newPhotos = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast({ title: 'Invalid file', description: `${file.name} is not an image`, variant: 'destructive' });
          continue;
        }
        if (file.size > 8 * 1024 * 1024) {
          toast({ title: 'File too large', description: `${file.name} exceeds 8 MB`, variant: 'destructive' });
          continue;
        }
        try {
          const res = await reviewsAPI.uploadPhoto(file);
          if (res.data?.url) {
            newPhotos.push({ url: `${API_URL}${res.data.url}`, file_id: res.data.file_id });
          }
        } catch (err) {
          toast({ title: 'Upload failed', description: err.response?.data?.detail || file.name, variant: 'destructive' });
        }
      }
      if (newPhotos.length) setPhotos(p => [...p, ...newPhotos]);
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await reviewsAPI.create({
        reviewee_id: userId,
        job_id: jobId || null,
        project_id: projectId || null,
        rating,
        title: title.trim(),
        content: content.trim(),
        photos: photos.map(p => p.url),
      });

      toast({
        title: 'Review Submitted',
        description: 'Your review has been posted successfully.',
      });

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to submit review',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6" data-testid="review-form">
      <h3 className="text-xl font-bold text-gray-900 mb-2">
        Write a Review for {userName}
      </h3>
      {jobTitle && (
        <p className="text-sm text-gray-600 mb-4">
          For job: <span className="font-medium">{jobTitle}</span>
        </p>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label className="text-base font-semibold">Rating *</Label>
          <div className="mt-2">
            <StarRatingInput rating={rating} setRating={setRating} disabled={submitting} />
          </div>
          {errors.rating && <p className="text-red-500 text-sm mt-1">{errors.rating}</p>}
        </div>

        <div>
          <Label htmlFor="title" className="text-base font-semibold">Review Title *</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Summarize your experience"
            className={`mt-2 ${errors.title ? 'border-red-500' : ''}`}
            disabled={submitting}
            data-testid="review-title-input"
          />
          {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
        </div>

        <div>
          <Label htmlFor="content" className="text-base font-semibold">Your Review *</Label>
          <Textarea
            id="content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Share details about your experience working with this person..."
            rows={4}
            className={`mt-2 ${errors.content ? 'border-red-500' : ''}`}
            disabled={submitting}
            data-testid="review-content-input"
          />
          <div className="flex justify-between mt-1">
            {errors.content ? (
              <p className="text-red-500 text-sm">{errors.content}</p>
            ) : <span />}
            <span className={`text-sm ${content.length < 10 ? 'text-gray-400' : 'text-green-600'}`}>
              {content.length}/10 min
            </span>
          </div>
        </div>

        {/* Photo Uploader (up to 3) */}
        <div>
          <Label className="text-base font-semibold">Photos <span className="text-gray-400 font-normal text-sm">(optional, max 3)</span></Label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/gif"
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid="review-photo-input"
          />
          {photos.length < 3 && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto || submitting}
              className="mt-2 w-full h-20 border-2 border-dashed border-gray-300 hover:border-blue-500 rounded-lg flex items-center justify-center gap-2 bg-gray-50 transition-all disabled:opacity-50"
              data-testid="review-upload-photo-btn"
            >
              {uploadingPhoto ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">Uploading...</span>
                </>
              ) : (
                <>
                  <Camera className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">Add photos of the finished work</span>
                </>
              )}
            </button>
          )}
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2" data-testid="review-photo-thumbnails">
              {photos.map((p, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg border-2 border-gray-200 overflow-hidden bg-gray-100 group">
                  <img src={p.url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  <button
                    type="button"
                    onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
                    className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center opacity-90 hover:opacity-100"
                    data-testid={`review-remove-photo-${i}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            type="submit"
            disabled={submitting}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            data-testid="submit-review-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Review'
            )}
          </Button>
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={submitting}
              className="flex-1"
            >
              Cancel
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

// Reviews List Component
export const ReviewsList = ({ userId, limit = 5 }) => {
  const [reviews, setReviews] = useState([]);
  const [ratingStats, setRatingStats] = useState({ avg_rating: 0, total_reviews: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchReviews();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [userId, page]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await reviewsAPI.getUserReviews(userId, { page, limit });
      setReviews(response.data.reviews || []);
      setRatingStats(response.data.rating_stats || { avg_rating: 0, total_reviews: 0 });
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reviews-list">
      {/* Rating Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-4xl font-bold text-gray-900">{ratingStats.avg_rating.toFixed(1)}</p>
            <StarRating rating={ratingStats.avg_rating} size="md" showNumber={false} />
            <p className="text-sm text-gray-600 mt-1">{ratingStats.total_reviews} reviews</p>
          </div>
        </div>
      </div>

      {/* Reviews */}
      {reviews.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No reviews yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div 
              key={review._id} 
              className="bg-white border border-gray-200 rounded-lg p-5"
              data-testid={`review-${review._id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {review.reviewer_name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 flex items-center gap-2">
                      {review.reviewer_name || 'Anonymous'}
                      {review.verified && (
                        <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-bold bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" title="Verified completed project">
                          <ShieldCheck className="h-3 w-3" /> Verified
                        </span>
                      )}
                    </p>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {review.reviewer_type}
                      </Badge>
                      <span className="text-xs text-gray-500 flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(review.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
                <StarRating rating={review.rating} size="sm" />
              </div>

              <h4 className="font-semibold text-gray-900 mb-2">{review.title}</h4>
              <p className="text-gray-600">{review.content}</p>
              {Array.isArray(review.photos) && review.photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid={`review-photos-${review._id}`}>
                  {review.photos.map((url, idx) => (
                    <a
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-20 h-20 rounded-lg border-2 border-gray-200 overflow-hidden bg-gray-100 hover:border-blue-500 transition-colors"
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <span className="flex items-center px-3 text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReviewsList;
